import { MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/system';
import { bindMenu, bindTrigger, usePopupState } from 'material-ui-popup-state/hooks';
import { useTranslation } from 'react-i18next';

import TicketCard from 'src/common/components/styled/TicketCard';
import { PARTICIPANT_LIST_QUERY_KEYS } from 'src/helpers/participantListQueryParams';
import { IUser } from 'src/interfaces/User';

import UserRoleChip from './UserRoleChip';

interface Props {
  user: IUser;
  canManage?: boolean;
  showViewParticipants?: boolean;
  onDelete: (id: string, e?: MouseEvent<unknown>) => void;
  onEdit: (id?: string, e?: MouseEvent<unknown>) => void;
}

const UserCard = ({
  user,
  canManage = false,
  showViewParticipants = false,
  onDelete,
  onEdit,
}: Props) => {
  const { t } = useTranslation();
  const { palette } = useTheme();
  const navigate = useNavigate();
  const toolsMenu = usePopupState({ variant: 'popover', popupId: `user-tools-${user.id}` });

  const handleViewParticipants = (e: MouseEvent<unknown>) => {
    e.stopPropagation();
    const params = new URLSearchParams();
    params.set(PARTICIPANT_LIST_QUERY_KEYS.ownerUserId, user.id);
    navigate(`/dashboard/system/participants?${params.toString()}`);
  };

  return (
    <TicketCard
      eyebrow={t('drawer.users')}
      title={user.name}
      description={user.email}
      avatar={
        <Avatar
          sx={{
            background: `linear-gradient(135deg, ${palette.success.dark} 0%, ${palette.success.light} 100%)`,
          }}
          aria-label={user.name}
        >
          {user.name.charAt(0).toUpperCase()}
        </Avatar>
      }
      badges={[
        {
          label: user.role?.name ?? t('drawer.users'),
          highlighted: true,
        },
      ]}
      actions={
        showViewParticipants ? (
          <Tooltip title={t('users.viewParticipants')}>
            <IconButton
              size="small"
              color="primary"
              aria-label={t('users.viewParticipants')}
              onClick={handleViewParticipants}
            >
              <PeopleOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : undefined
      }
      footer={
        canManage ? (
          <>
            <IconButton {...bindTrigger(toolsMenu)} edge="start" size="small">
              <MoreVertIcon />
            </IconButton>
            <Menu {...bindMenu(toolsMenu)}>
              <MenuItem
                onClick={e => {
                  onEdit(user.id, e);
                  toolsMenu.close();
                }}
              >
                <ListItemIcon>
                  <EditIcon color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('users.editUser')}</ListItemText>
              </MenuItem>
              <Tooltip title={t('users.deleteUser')}>
                <MenuItem
                  onClick={e => {
                    onDelete(user.id, e);
                    toolsMenu.close();
                  }}
                >
                  <ListItemIcon>
                    <DeleteForeverIcon color="error" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>{t('users.delete')}</ListItemText>
                </MenuItem>
              </Tooltip>
            </Menu>
            <Box sx={{ flex: 1 }} />
            <UserRoleChip role={user.role} />
          </>
        ) : (
          <UserRoleChip role={user.role} />
        )
      }
    />
  );
};

export default UserCard;
